import { Dispatch, ReactElement, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { UiInput, UiInputSize } from '@nx-frontend/shared/ui/input';
import { UiSelect } from '@nx-frontend/shared/ui/select';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { generalEnv } from '@nx-frontend/shared/env';
import { OrgMemberRole, OrgMemberRoleLabelLocKeyMap, PermissionsAccessMode } from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { OrgMemberRoleForInvite } from '../store';

export interface InviteItemModel {
  id: string;
  role: OrgMemberRoleForInvite;
  email: string;
  accessMode?: PermissionsAccessMode;
  isError: boolean;
  errorText: string;
}
type LastAction = 'add' | 'remove' | null;
export interface ItemProps {
  index: number;
  item: InviteItemModel;
  onRemoveItem: (id: string) => void;
  onUpdateItem: (index: number, newItem: InviteItemModel) => void;
  disabled: boolean;
  items: InviteItemModel[];
  lastAction: string;
  hideClient: boolean;
  availablePortalAccessRoles: OrgMemberRole[];
  setLastAction: Dispatch<SetStateAction<LastAction>>;
  isOnlySharedAvailable?: boolean;
}

const selectTooltipRestrictedRole = (
  <div>
    <span>
      Access for this role is <br /> restricted. You can change <br /> it in the Portal settings , or <br />{' '}
      alternatively, elevate the <br /> role for this user.
    </span>
  </div>
);

export function Item({
  index,
  item,
  onRemoveItem,
  onUpdateItem,
  disabled,
  items,
  lastAction,
  hideClient,
  availablePortalAccessRoles,
  setLastAction,
  isOnlySharedAvailable,
}: ItemProps) {
  const t = useTranslations();
  const [email, setEmail] = useState(item.email);
  const lastElementRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (lastAction === 'add' && lastElementRef.current) {
      lastElementRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    setLastAction(null);
  }, [items, lastAction, setLastAction]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    onUpdateItem(index, { ...item, email: e.target.value, errorText: '', isError: false });
  };

  const handleOptionChange = (value: OrgMemberRoleForInvite) => {
    onUpdateItem(index, { ...item, role: value });
  };

  const handleAccessChange = (value: PermissionsAccessMode) => {
    onUpdateItem(index, { ...item, accessMode: value });
  };

  const showAccessModeSelect = 'accessMode' in item;
  const tooltipHtml = t('features.manage-members.role_tooltip');
  const tooltipRoleContent = (
    <div>
      <span dangerouslySetInnerHTML={{ __html: tooltipHtml }}></span>
      <br />
      <Link
        target="_blank"
        href={`https://${generalEnv.nimbusDomain}/guides/getting-started/key-role-differences/`}
        className="text-teal-500 hover:text-teal-400 ml-1 border-b border-teal-400"
      >
        {t('features.manage-members.learn_about_roles')}
      </Link>
    </div>
  );

  const tooltipAccessContent = (
    <div
      className="max-w-56"
      dangerouslySetInnerHTML={{ __html: t('features.manage-members.access_to_portal_content_tooltip') }}
    ></div>
  );

  const getOptionRoleRestrictedMessage = useCallback((label: string): ReactElement => {
    return (
      <div className="flex items-center">
        <span>{label}</span>
        <div className="ml-auto text-neutral-500">
          <TippyTooltip
            message={selectTooltipRestrictedRole}
            position={UiTooltipPosition.TOP}
            show={true}
            interactive={false}
          >
            <div>
              <Icon icon={IconsMap.INFO_FILL} className="w-4 h-4" />
            </div>
          </TippyTooltip>
        </div>
      </div>
    );
  }, []);

  const getRoleOptions = useCallback(
    (roles: OrgMemberRoleForInvite[]) => {
      return roles.map((role) => {
        const label = t(OrgMemberRoleLabelLocKeyMap[role]);
        const content = !availablePortalAccessRoles.includes(role) ? getOptionRoleRestrictedMessage(label) : undefined;
        return { value: role, label, content };
      });
    },
    [availablePortalAccessRoles, getOptionRoleRestrictedMessage, t]
  );

  const roleOptions: { value: OrgMemberRoleForInvite; label: string; content?: ReactElement }[] = useMemo(() => {
    if (hideClient) {
      return getRoleOptions([OrgMemberRole.MANAGER, OrgMemberRole.MEMBER]);
    }
    return getRoleOptions([OrgMemberRole.MANAGER, OrgMemberRole.MEMBER, OrgMemberRole.CLIENT]);
  }, [getRoleOptions, hideClient]);

  const accessOptions = isOnlySharedAvailable
    ? [
        {
          value: PermissionsAccessMode.FULL_ACCESS,
          label: t('features.manage-members.full_access'),
        },
        {
          value: PermissionsAccessMode.SHARED_ACCESS,
          label: t('features.manage-members.only_shared_items'),
        },
      ]
    : [{ value: PermissionsAccessMode.FULL_ACCESS, label: t('features.manage-members.full_access') }];

  const selectedRoleContent = !availablePortalAccessRoles.includes(item.role)
    ? getOptionRoleRestrictedMessage(t(OrgMemberRoleLabelLocKeyMap[item.role]))
    : undefined;

  return (
    <>
      {index === 0 && (
        <div className="flex item-center gap-2.5">
          <label className="flex text-gray-500 items-start text-xs gap-2 mr-24">
            {t('features.manage-members.role')}{' '}
            <TippyTooltip message={tooltipRoleContent} position={UiTooltipPosition.TOP} show={true} interactive={true}>
              <div>
                <Icon icon={IconsMap.QUESTION} className="w-[16px] text-gray-500 cursor-pointer" />
              </div>
            </TippyTooltip>
          </label>
          <label className="flex text-gray-500 text-xs mr-[320px]">{t('features.manage-members.member_email')}</label>
          {showAccessModeSelect && (
            <label className="flex text-gray-500 items-start text-xs gap-2">
              {t('features.manage-members.access_to_portal_content')}
              <TippyTooltip
                message={tooltipAccessContent}
                position={UiTooltipPosition.TOP}
                show={true}
                interactive={false}
              >
                <div>
                  <Icon icon={IconsMap.QUESTION} className="w-[16px] text-gray-500 cursor-pointer" />{' '}
                </div>
              </TippyTooltip>
            </label>
          )}
        </div>
      )}

      <div className="flex w-full max-h-16 gap-2.5" ref={index === items.length - 1 ? lastElementRef : null}>
        <div className="grow-0 shrink-0 basis-36 mb-2">
          <UiSelect<OrgMemberRoleForInvite>
            onChange={(value) => handleOptionChange(value)}
            padding="py-1.5 px-2"
            value={item.role}
            options={roleOptions}
          >
            {selectedRoleContent}
          </UiSelect>
        </div>
        <div className="w-full">
          <UiInput
            type="text"
            value={email}
            placeholder="example@acme.com"
            error={item.isError}
            errorText={item.errorText}
            size={UiInputSize.MEDIUM}
            onChange={handleEmailChange}
          />
        </div>
        {showAccessModeSelect && (
          <div className="min-w-[192px] shrink-0">
            <UiSelect<PermissionsAccessMode>
              onChange={(value) => handleAccessChange(value)}
              padding="py-1.5 px-2"
              value={item.accessMode}
              options={accessOptions}
            />
          </div>
        )}

        <div className="flex justify-center items-center">
          <button onClick={() => onRemoveItem(item.id)} disabled={disabled}>
            <Icon
              icon={IconsMap.TRASH}
              className={classNames('w-8 h-4', {
                'text-cyan-900': !disabled,
                'mb-2': !item.isError,
                'mb-6': item.isError,
                'text-grey-400': disabled,
              })}
            />
          </button>
        </div>
      </div>
    </>
  );
}
